import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignIn, useUser, useClerk } from '@clerk/clerk-react';

// Clerk user IDs (user_...) allowed into the admin dashboard. This client-side
// gate is for UX only — the real enforcement is the is_admin() RLS policies in
// Supabase, which check the same IDs server-side.
const ADMIN_IDS = (process.env.REACT_APP_ADMIN_USER_IDS || '')
  .split(',').map(s => s.trim()).filter(Boolean);

const formatPhone = (input) => {
  const digits = input.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export default function AdminLogin() {
  const navigate = useNavigate();
  const { signIn, setActive, isLoaded } = useSignIn();
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Already signed in with Clerk (e.g. returning admin) — verify and enter.
  useEffect(() => {
    if (!isSignedIn || !user) return;
    if (ADMIN_IDS.includes(user.id)) {
      sessionStorage.setItem('admin_phone', user.primaryPhoneNumber?.phoneNumber || user.id);
      navigate('/admin');
    } else {
      setError('This account is not authorized for admin access.');
      signOut();
    }
  }, [isSignedIn, user, navigate, signOut]);

  const handleSendCode = async () => {
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) {
      setError('Enter a valid 10-digit phone number');
      return;
    }
    if (!isLoaded || busy) return;
    setBusy(true);
    setError('');
    try {
      const attempt = await signIn.create({ identifier: '+1' + digits });
      const factor = attempt.supportedFirstFactors.find(f => f.strategy === 'phone_code');
      if (!factor) throw new Error('Phone sign-in is not enabled for this account.');
      await signIn.prepareFirstFactor({ strategy: 'phone_code', phoneNumberId: factor.phoneNumberId });
      setStep('otp');
    } catch (e) {
      setError(e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || e.message || 'Could not send code');
    }
    setBusy(false);
  };

  const handleVerify = async () => {
    if (otp.length < 6) {
      setError('Enter the 6-digit code');
      return;
    }
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await signIn.attemptFirstFactor({ strategy: 'phone_code', code: otp });
      if (res.status === 'complete') {
        await setActive({ session: res.createdSessionId });
        // The isSignedIn effect above performs the admin check and navigation.
      } else {
        setError('Verification incomplete — try again.');
      }
    } catch (e) {
      setError(e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || 'Invalid code');
    }
    setBusy(false);
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.logo}>candid. <span style={styles.admin}>admin</span></div>

        {step === 'phone' ? (
          <>
            <h2 style={styles.title}>Admin sign in</h2>
            <p style={styles.subtitle}>Enter your phone number to receive a verification code.</p>
            <div style={styles.phoneRow}>
              <div style={styles.cc}>🇺🇸 +1</div>
              <input
                style={styles.input}
                placeholder="(555) 123-4567"
                value={phone}
                onChange={e => setPhone(formatPhone(e.target.value))}
                onKeyDown={e => e.key === 'Enter' && handleSendCode()}
                autoFocus
              />
            </div>
            {error && <p style={styles.error}>{error}</p>}
            <button style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }} onClick={handleSendCode} disabled={busy}>
              {busy ? 'Sending…' : 'Send code →'}
            </button>
          </>
        ) : (
          <>
            <h2 style={styles.title}>Enter code</h2>
            <p style={styles.subtitle}>Code sent to +1 {phone}</p>
            <input
              style={styles.otpInput}
              placeholder="000000"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              autoFocus
            />
            {error && <p style={styles.error}>{error}</p>}
            <button style={{ ...styles.btn, opacity: busy ? 0.6 : 1 }} onClick={handleVerify} disabled={busy}>
              {busy ? 'Verifying…' : 'Verify →'}
            </button>
            <button style={styles.linkBtn} onClick={() => { setStep('phone'); setOtp(''); setError(''); }}>
              Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#030010', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui, -apple-system, sans-serif' },
  card: { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 16, padding: 40, width: 400, maxWidth: '90vw' },
  logo: { fontSize: 32, fontWeight: 900, letterSpacing: -1.5, background: 'linear-gradient(90deg, #a78bfa, #f472b6, #fb923c)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: 32 },
  admin: { fontSize: 14, fontWeight: 500, color: '#888', WebkitTextFillColor: '#888', background: 'none', letterSpacing: 0, marginLeft: 8 },
  title: { color: '#fff', fontSize: 24, fontWeight: 800, margin: '0 0 8px 0' },
  subtitle: { color: '#888', fontSize: 14, margin: '0 0 24px 0', lineHeight: 1.5 },
  phoneRow: { display: 'flex', gap: 0, marginBottom: 16, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, overflow: 'hidden', background: 'rgba(255,255,255,0.03)' },
  cc: { padding: '14px 16px', color: '#fff', fontWeight: 600, fontSize: 16, borderRight: '1px solid rgba(255,255,255,0.1)' },
  input: { flex: 1, background: 'transparent', border: 'none', color: '#fff', fontSize: 16, padding: '14px 16px', outline: 'none' },
  otpInput: { width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#fff', fontSize: 28, padding: 18, textAlign: 'center', letterSpacing: 12, marginBottom: 16, outline: 'none' },
  btn: { width: '100%', padding: 16, borderRadius: 999, background: 'linear-gradient(90deg, #a78bfa, #f472b6)', color: '#fff', fontSize: 15, fontWeight: 700, border: 'none', cursor: 'pointer' },
  linkBtn: { width: '100%', padding: 12, marginTop: 8, background: 'none', color: '#a78bfa', fontSize: 13, border: 'none', cursor: 'pointer' },
  error: { color: '#f87171', fontSize: 13, margin: '8px 0 0 0', textAlign: 'center' },
};
